import { memo, useCallback, useState } from 'react'

import { AlarmOn, Cached, ControlPointDuplicate, Info } from '@mui/icons-material'
import { Box, Divider, ListItemIcon, MenuItem, Skeleton, Tooltip, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { NewDialog } from 'domains/Workspaces/Events'
import { useEventCreateMutation, useEventDeleteMutation, useEventWakeMutation } from 'hooks/mutations/smep'
import { eventsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events'
import { eventRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
  const [editDialogOpen, setEditDialogOpen] = useState(false)

  const { eventId } = eventRoute.useParams()
  const navigate = useNavigate({ from: eventRoute.fullPath })

  const eventQuery = useQuery(eventQueryKeys.detail({ eventId }))
  const { data: event } = eventQuery

  const { mutate: createSmepEvent, ...smepEventCreateMutation } = useEventCreateMutation({ originId: eventId })
  const { mutate: deleteEvent } = useEventDeleteMutation()
  const { mutate: wakeEvent } = useEventWakeMutation()

  const handleDeletion = useCallback(() => {
    if (!event) return

    setAnchorEl(undefined)

    deleteEvent(
      { eventId },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Workspace.Event.nameDelete', { name: event.name }), { variant: 'success' })
          setTimeout(() => {
            navigate({ to: eventsOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteEvent, enqueueSnackbar, event, eventId, navigate, t])

  const handleWakeEvent = useCallback(() => {
    setAnchorEl(undefined)

    wakeEvent(
      { eventId },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Workspace.Event.eventReprocessed', { count: 1 }), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [enqueueSnackbar, eventId, t, wakeEvent])

  const handleDuplicateSmepEvent = useCallback(() => {
    if (!event) return

    setAnchorEl(undefined)

    createSmepEvent(
      { smepEventInsert: { ...event, name: `${event.name} (${t('duplicate')})` } },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [createSmepEvent, enqueueSnackbar, event, t])

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
        <Tooltip sx={{ cursor: 'help' }} title={t('domain:Workspace.Event.updateInformation')} placement="bottom" arrow>
          <Info color="info" />
        </Tooltip>

        <AnimatedButton
          open={!!anchorEl}
          IconButtonProps={{
            onClick: (e) => setAnchorEl(e.currentTarget),
          }}
        />
      </Box>
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={event?.name}
        entity={t('entity:smep.event', { count: 1 })}
      >
        <MenuItem onClick={handleWakeEvent}>
          <ListItemIcon>
            <AlarmOn />
          </ListItemIcon>
          <Typography variant="text">{t('domain:Workspace.Event.reprocess')}</Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAnchorEl(undefined)

            setTimeout(() => {
              setEditDialogOpen(true)
            }, 300)
          }}
        >
          <ListItemIcon>
            <Cached />
          </ListItemIcon>
          <Typography variant="text">{t('domain:Workspace.Event.configureSetup')}</Typography>
        </MenuItem>

        <MenuItem onClick={handleDuplicateSmepEvent} disabled={smepEventCreateMutation.isPending}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {smepEventCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={eventId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:Workspace.Event.deleteEvent', { count: 1 })}
          onConfirm={handleDeletion}
        />
      </ActionMenu>
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <NewDialog initialValue={event} />
      </Dialog>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
